import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Search, Users as UsersIcon, ListTodo, FolderKanban, Clock, X, AlertTriangle, CheckCircle2, Gift, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";
import TenantInviteDialog from "./TenantInviteDialog";
import BulkDeleteDialog from "../../components/platform/BulkDeleteDialog";

const STATUS_TONE = {
  trial: "bg-violet-100 text-violet-700 ring-violet-200",
  active: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  past_due: "bg-amber-100 text-amber-700 ring-amber-200",
  suspended: "bg-red-100 text-red-700 ring-red-200",
  canceled: "bg-zinc-100 text-zinc-600 ring-zinc-200",
};

const STATUSES = ["all", "trial", "active", "past_due", "suspended", "canceled"];

/**
 * Human-readable "how long until trial ends" for the tenant list. Uses
 * days for anything under 60 days and months otherwise (so "6 aylık davet"
 * shows as "6 ay kaldı" instead of "182 gün kaldı" which is confusing).
 * Expired trials render in amber with "N gün önce bitti".
 */
function formatTrialRemaining(iso) {
  if (!iso) return null;
  const end = new Date(iso);
  const now = new Date();
  const diffMs = end - now;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `${Math.abs(diffDays)} gün önce bitti`;
  }
  if (diffDays === 0) return "Bugün bitiyor";
  if (diffDays === 1) return "1 gün kaldı";
  if (diffDays < 60) return `${diffDays} gün kaldı`;
  const months = Math.round(diffDays / 30);
  return `~${months} ay kaldı`;
}

export default function PlatformTenants() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Server-side debounced search — client-side alone missed tenants
  // beyond the old 1000-row cap (Boggy bug, 2026-02-14).
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [counts, setCounts] = useState({ all: 0, trial: 0, active: 0, past_due: 0, suspended: 0, canceled: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "all";
  const [detailId, setDetailId] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  // Bulk-selection state — driven by a Set so add/remove is O(1).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (all) => {
    if (all) setSelectedIds(new Set(tenants.map((tn) => tn.id)));
    else setSelectedIds(new Set());
  };

  // When the visible list changes (filter / search / status tab), any
  // ids that fell out of view stay technically selected — surface that
  // as a badge on the bulk bar so admins don't accidentally delete
  // tenants they can't see.
  const runBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const { data } = await api.post("/platform/tenants/bulk-delete", { ids });
    const removed = data.deleted_tenants || 0;
    const skipped = data.skipped || [];
    if (removed) {
      toast.success(`${removed} tenant silindi`);
    }
    if (skipped.length) {
      const seedReason = skipped.find((s) => s.reason === "protected_seed_tenant");
      if (seedReason) {
        toast.warning(`Bazı tenant'lar korumalı olduğu için silinmedi (${seedReason.name || "Acme"})`);
      } else {
        toast.warning(`${skipped.length} tenant silinemedi`);
      }
    }
    setSelectedIds(new Set());
    load();
    loadCounts();
  };

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const params = {};
      const q = opts.q ?? debouncedSearch;
      const f = opts.filter ?? filter;
      if (q && q.trim()) params.q = q.trim();
      if (f && f !== "all") params.status = f;
      const { data } = await api.get("/platform/tenants", { params });
      setTenants(data || []);
    } finally {
      setLoading(false);
    }
  };

  // Authoritative status tab counts — sourced from a Mongo aggregation
  // so the "Tümü 1657 · trial 1240 · ..." labels are correct even when
  // the visible list is filtered or capped.
  const loadCounts = async () => {
    try {
      const { data } = await api.get("/platform/tenants/counts");
      if (data) setCounts(data);
    } catch {
      /* silent — tabs will fall back to zeros */
    }
  };

  useEffect(() => {
    loadCounts();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    load();
  }, [debouncedSearch, filter]);

  const setFilter = (v) => {
    const nx = new URLSearchParams(searchParams);
    if (v === "all") nx.delete("filter");
    else nx.set("filter", v);
    setSearchParams(nx, { replace: true });
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/platform/tenants/${id}/set-status`, { status });
      toast.success(t("platform.tenants.status_updated"));
      load();
      loadCounts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const extend = async (id) => {
    try {
      await api.post(`/platform/tenants/${id}/extend-trial`, { days: 14 });
      toast.success(t("platform.tenants.trial_extended"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleFeature = async (id, feature, enabled) => {
    // Optimistic update — flip the flag in the local list immediately so the
    // pill re-renders without waiting for the round-trip.
    setTenants((cur) =>
      cur.map((x) => {
        if (x.id !== id) return x;
        const set = new Set(x.enabled_features || []);
        if (enabled) set.add(feature);
        else set.delete(feature);
        return { ...x, enabled_features: Array.from(set).sort() };
      }),
    );
    try {
      await api.post(`/platform/tenants/${id}/features`, {
        feature,
        enabled,
      });
      toast.success(
        enabled ? `${feature.toUpperCase()} açıldı` : `${feature.toUpperCase()} kapatıldı`,
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      // rollback on failure
      load();
    }
  };

  // Server already applies status + search filters, so we just render
  // whatever the API returned. Keeping the memo as a pass-through so the
  // rest of the JSX doesn't need to change.
  const filtered = tenants;

  return (
    <div className="space-y-5" data-testid="platform-tenants">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">
            Serpix Admin
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            {t("platform.nav.tenants")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t("platform.tenants.subtitle", { count: counts.all })}
          </p>
        </div>
        <Button
          onClick={() => setShowInvite(true)}
          data-testid="platform-tenants-invite-btn"
          className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
        >
          <Gift className="mr-1.5 h-4 w-4" />
          {t("platform.invitations.new")}
        </Button>
      </header>

      {/* Status tabs */}
      <div className="flex flex-wrap items-center gap-2" data-testid="platform-tenants-tabs">
        {STATUSES.map((s) => {
          const active = filter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`platform-tenants-tab-${s}`}
              className={[
                "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                active
                  ? "border-violet-600 bg-violet-600 text-white shadow-md shadow-violet-500/20"
                  : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50",
              ].join(" ")}
            >
              <span>{s === "all" ? t("platform.tenants.all") : s}</span>
              <span
                className={[
                  "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold",
                  active ? "bg-white/95 text-violet-700" : "bg-zinc-100 text-zinc-700",
                ].join(" ")}
              >
                {counts[s] || 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="platform-tenants-search"
            placeholder={t("platform.tenants.search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/70 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="w-10 px-3 py-3">
                  <Checkbox
                    data-testid="platform-tenants-select-all"
                    checked={
                      tenants.length > 0 &&
                      tenants.every((tn) => selectedIds.has(tn.id))
                    }
                    onCheckedChange={(v) => toggleSelectAll(!!v)}
                    aria-label="Tümünü seç"
                  />
                </th>
                <th className="px-6 py-3 text-left font-semibold">{t("platform.tenants.col_tenant")}</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">{t("platform.tenants.col_users")}</th>
                <th className="px-4 py-3 text-right font-semibold">{t("platform.tenants.col_tasks")}</th>
                <th className="px-6 py-3 text-right font-semibold">{t("platform.tenants.col_actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-sm text-zinc-500">
                    {t("common.loading")}
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-sm text-zinc-400">
                    {t("platform.tenants.empty")}
                  </td>
                </tr>
              )}
              {filtered.map((tn) => (
                <tr
                  key={tn.id}
                  className={`cursor-pointer hover:bg-zinc-50/60 ${
                    selectedIds.has(tn.id) ? "bg-violet-50/40" : ""
                  }`}
                  data-testid={`platform-tenant-row-${tn.id}`}
                  onClick={() => setDetailId(tn.id)}
                >
                  <td
                    className="w-10 px-3 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      data-testid={`platform-tenant-select-${tn.id}`}
                      checked={selectedIds.has(tn.id)}
                      onCheckedChange={() => toggleSelect(tn.id)}
                      aria-label={`${tn.name} seç`}
                    />
                  </td>
                  <td className="px-6 py-3">
                    <div className="font-medium text-zinc-900">{tn.name}</div>
                    <div className="mt-0.5 font-mono text-[10px] text-zinc-400">{tn.id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={tn.subscription_status} tenantId={tn.id} />
                    {tn.subscription_status === "trial" && tn.trial_ends_at && (
                      <div
                        className="mt-1 text-[10px] text-zinc-500"
                        data-testid={`platform-trial-remaining-${tn.id}`}
                        title={new Date(tn.trial_ends_at).toLocaleString()}
                      >
                        {formatTrialRemaining(tn.trial_ends_at)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-700">{tn.user_count}</td>
                  <td className="px-4 py-3 text-right text-zinc-700">{tn.task_count}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const on = (tn.enabled_features || []).includes("cop");
                        return (
                          <button
                            type="button"
                            onClick={() => toggleFeature(tn.id, "cop", !on)}
                            data-testid={`platform-cop-toggle-${tn.id}`}
                            title="COP workspace"
                            className={
                              "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider transition-colors " +
                              (on
                                ? "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                                : "border-zinc-200 bg-white text-zinc-400 hover:bg-zinc-50")
                            }
                          >
                            <span
                              className={
                                "h-1.5 w-1.5 rounded-full " +
                                (on ? "bg-violet-500" : "bg-zinc-300")
                              }
                            />
                            COP
                          </button>
                        );
                      })()}
                      {(() => {
                        const on = (tn.enabled_features || []).includes("expo");
                        return (
                          <button
                            type="button"
                            onClick={() => toggleFeature(tn.id, "expo", !on)}
                            data-testid={`platform-expo-toggle-${tn.id}`}
                            title="EXPO workspace"
                            className={
                              "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider transition-colors " +
                              (on
                                ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                : "border-zinc-200 bg-white text-zinc-400 hover:bg-zinc-50")
                            }
                          >
                            <span
                              className={
                                "h-1.5 w-1.5 rounded-full " +
                                (on ? "bg-emerald-500" : "bg-zinc-300")
                              }
                            />
                            EXPO
                          </button>
                        );
                      })()}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => extend(tn.id)}
                        data-testid={`platform-extend-trial-${tn.id}`}
                        className="text-xs"
                        title="Bu tenant'ın trial süresine 14 gün ekler (mevcut süreyi silmez)"
                      >
                        {t("platform.extend_trial")}
                      </Button>
                      <Select
                        onValueChange={(v) => setStatus(tn.id, v)}
                        value={tn.subscription_status}
                      >
                        <SelectTrigger
                          className="w-36"
                          data-testid={`platform-set-status-${tn.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.slice(1).map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="divide-y divide-zinc-100 md:hidden">
          {loading && (
            <div className="px-4 py-6 text-center text-sm text-zinc-500">
              {t("common.loading")}
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-zinc-400">
              {t("platform.tenants.empty")}
            </div>
          )}
          {filtered.map((tn) => (
            <div
              key={tn.id}
              onClick={() => setDetailId(tn.id)}
              className={`flex cursor-pointer gap-3 px-4 py-3 ${
                selectedIds.has(tn.id) ? "bg-violet-50/40" : ""
              }`}
              data-testid={`platform-tenant-card-${tn.id}`}
            >
              <div
                className="pt-0.5"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  data-testid={`platform-tenant-select-mobile-${tn.id}`}
                  checked={selectedIds.has(tn.id)}
                  onCheckedChange={() => toggleSelect(tn.id)}
                  aria-label={`${tn.name} seç`}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-zinc-900">{tn.name}</div>
                    <div className="mt-0.5 text-[10px] text-zinc-400">{tn.id.slice(0, 12)}...</div>
                  </div>
                  <StatusBadge status={tn.subscription_status} tenantId={tn.id} />
                </div>
                <div className="mt-2 flex items-center gap-3 text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1">
                    <UsersIcon className="h-3 w-3" /> {tn.user_count}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <ListTodo className="h-3 w-3" /> {tn.task_count}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating bulk-delete bar — appears when 1+ tenants selected */}
      {selectedIds.size > 0 && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4"
          data-testid="platform-tenants-bulk-bar"
        >
          <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-zinc-800/10 bg-zinc-900 px-4 py-2 text-white shadow-lg shadow-zinc-950/20">
            <span className="text-sm font-medium">
              <span
                className="tabular-nums"
                data-testid="platform-tenants-selected-count"
              >
                {selectedIds.size}
              </span>{" "}
              tenant seçili
            </span>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-zinc-300 underline-offset-4 hover:text-white hover:underline"
              data-testid="platform-tenants-clear-selection"
            >
              Seçimi kaldır
            </button>
            <div className="mx-1 h-4 w-px bg-white/20" />
            <Button
              size="sm"
              onClick={() => setConfirmBulkDelete(true)}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="platform-tenants-bulk-delete-btn"
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Toplu Sil
            </Button>
          </div>
        </div>
      )}

      <BulkDeleteDialog
        open={confirmBulkDelete}
        onOpenChange={setConfirmBulkDelete}
        kind="tenant"
        count={selectedIds.size}
        onConfirm={runBulkDelete}
      />

      <TenantDetailDrawer
        tenantId={detailId}
        onClose={() => setDetailId(null)}
        onReload={load}
      />

      <TenantInviteDialog
        open={showInvite}
        onOpenChange={setShowInvite}
        onCreated={() => load()}
      />
    </div>
  );
}

function StatusBadge({ status, tenantId }) {
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        STATUS_TONE[status] || "bg-zinc-100 text-zinc-700 ring-zinc-200",
      ].join(" ")}
      data-testid={
        tenantId
          ? `platform-status-badge-${tenantId}`
          : `platform-status-badge-${status}`
      }
    >
      <span
        className={
          "inline-block h-1.5 w-1.5 rounded-full " +
          (status === "active"
            ? "bg-emerald-500"
            : status === "past_due"
              ? "bg-amber-500"
              : status === "suspended"
                ? "bg-red-500"
                : status === "canceled"
                  ? "bg-zinc-400"
                  : "bg-violet-500")
        }
      />
      {status}
    </span>
  );
}

function TenantDetailDrawer({ tenantId, onClose, onReload }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    if (!tenantId) {
      setDetail(null);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/platform/tenants/${tenantId}/detail`);
        setDetail(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  const runDigest = async () => {
    if (!tenantId) return;
    setSendingDigest(true);
    try {
      await api.post(`/platform/tenants/${tenantId}/send-digest`);
      toast.success(t("platform.tenants.digest_sent"));
      onReload?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendingDigest(false);
    }
  };

  return (
    <Sheet open={!!tenantId} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="platform-tenant-detail">
        <SheetHeader>
          <SheetTitle>{detail?.tenant?.name || "…"}</SheetTitle>
          <SheetDescription>
            {detail?.tenant?.slug && (
              <span className="font-mono text-[11px]">{detail.tenant.slug}</span>
            )}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="mt-6 text-sm text-zinc-500">{t("common.loading")}</div>
        )}

        {detail && (
          <div className="mt-6 space-y-5">
            <div>
              <StatusBadge status={detail.tenant.subscription_status} />
              <div className="mt-2 text-xs text-zinc-500">
                {detail.tenant.timezone} · {t("platform.tenants.created")}{" "}
                {new Date(detail.tenant.created_at).toLocaleDateString()}
                {detail.tenant.trial_ends_at && (
                  <>
                    {" · "}
                    <Clock className="inline h-3 w-3" /> trial ends{" "}
                    {new Date(detail.tenant.trial_ends_at).toLocaleDateString()}
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <StatBlock icon={UsersIcon} label={t("platform.tenants.detail_users")} value={detail.user_count} />
              <StatBlock icon={CheckCircle2} label={t("platform.tenants.detail_admins")} value={detail.admin_count} />
              <StatBlock icon={FolderKanban} label={t("platform.tenants.detail_projects")} value={detail.projects_count} />
              <StatBlock icon={ListTodo} label={t("platform.tenants.detail_open")} value={`${detail.open_tasks} / ${detail.total_tasks}`} />
              {detail.overdue_tasks > 0 && (
                <div className="col-span-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-800">
                  <AlertTriangle className="mr-1 inline h-3 w-3" />
                  {detail.overdue_tasks} {t("platform.tenants.overdue")}
                </div>
              )}
              {detail.last_login_at && (
                <div className="col-span-2 text-xs text-zinc-500">
                  {t("platform.tenants.last_login")}: {new Date(detail.last_login_at).toLocaleString()}
                </div>
              )}
            </div>

            {detail.admins?.length > 0 && (
              <div>
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                  {t("platform.tenants.detail_admins")}
                </h4>
                <ul className="space-y-1.5">
                  {detail.admins.map((a) => (
                    <li key={a.id} className="flex items-center gap-2 rounded-md bg-zinc-50 px-3 py-1.5 text-sm">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-100 text-[11px] font-semibold text-violet-700">
                        {(a.name || a.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-zinc-900">{a.name || a.email}</div>
                        <div className="truncate text-[11px] text-zinc-500">{a.email}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={runDigest}
                disabled={sendingDigest}
                data-testid="platform-tenant-send-digest"
              >
                {sendingDigest
                  ? t("common.loading")
                  : t("platform.tenants.send_digest")}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StatBlock({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2.5">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 font-display text-lg font-bold text-zinc-950">{value}</div>
    </div>
  );
}
